/**
 * Payment Method Modal
 * Searchable list of payment methods with icons.
 * Follows the same pattern as FiatCurrencyModal / CryptoCurrencyModal.
 */

import { useState, useMemo } from 'react';
import type { SupportedPaymentMethod } from '@nowramp/sdk';
import { useT } from '../../i18n';
import { CloseButton } from '../ui/CloseButton';
import { SearchInput } from '../ui/SearchInput';
import { NRLogo } from '../ui/NRLogo';
import { getMethodIcon, getMethodDisplayName } from '../PaymentMethodSelector';

export interface PaymentMethodModalProps {
	methods: SupportedPaymentMethod[];
	selectedId: string;
	onSelect: (id: string) => void;
	onClose: () => void;
}

export function PaymentMethodModal({
	methods,
	selectedId,
	onSelect,
	onClose,
}: PaymentMethodModalProps) {
	const t = useT();
	const [search, setSearch] = useState('');

	// The first method from the API is the recommended one (before reordering)
	const recommendedId = methods.length > 0 ? methods[0].id : '';

	const filtered = useMemo(() => {
		const raw = search.trim().toLowerCase();

		// If no search, put selected item first
		if (!raw) {
			if (selectedId) {
				const selected = methods.find((m) => m.id === selectedId);
				if (selected) {
					const rest = methods.filter((m) => m.id !== selectedId);
					return [selected, ...rest];
				}
			}
			return methods;
		}

		return methods.filter(
			(m) =>
				m.id.toLowerCase().includes(raw) ||
				m.name.toLowerCase().includes(raw) ||
				(m.description && m.description.toLowerCase().includes(raw))
		);
	}, [methods, search, selectedId]);

	return (
		<>
			{/* Modal header — 128px with blurred bg */}
			<div className="relative shrink-0" style={{ height: 128, background: 'var(--t-header-bg)', backdropFilter: 'blur(var(--t-header-blur))', WebkitBackdropFilter: 'blur(var(--t-header-blur))' }}>
				<div className="absolute" style={{ left: 12, top: 20 }}>
					<NRLogo width={46} />
				</div>
				<p className="absolute text-[16px] text-center truncate" style={{ top: 28, left: 64, right: 64, color: 'var(--t-text)', lineHeight: '24px' }}>
					{t('modal.choosePayment')}
				</p>
				<div className="absolute" style={{ right: 12, top: 20 }}>
					<CloseButton onClick={onClose} />
				</div>
				<div className="absolute" style={{ bottom: 8, left: 12, right: 12 }}>
					<SearchInput value={search} onChange={setSearch} inline />
				</div>
			</div>
			<div className="flex-1 overflow-y-auto custom-scroll px-3">
				{filtered.map((m, i) => {
					const isSelected = m.id === selectedId;
					const shortName = getMethodDisplayName(m.id, m.name, t);
					return (
						<button
							key={m.id}
							onClick={() => onSelect(m.id)}
							className="flex items-center w-full card-press hover-item modal-item-in"
							style={{ gap: 16, animationDelay: `${Math.min(i * 45, 350)}ms` }}
						>
							<span
								className="flex items-center justify-center shrink-0"
								style={{ width: 32, height: 32 }}
							>
								{getMethodIcon(m.id, m.icon, 32)}
							</span>
							<div className="flex-1 flex items-center" style={{ borderBottom: '1px solid var(--t-card-border)', paddingTop: 20, paddingBottom: 20, gap: 10 }}>
								<span className="flex-1 text-[18px] text-left" style={{ color: 'var(--t-text)', lineHeight: '20px' }}>
									{shortName}
								</span>
								{m.id === recommendedId && (
									<span className="text-[11px] font-medium" style={{ border: '1.5px solid var(--t-card-border)', borderRadius: 100, paddingLeft: 8, paddingRight: 8, color: 'var(--t-text-secondary)', lineHeight: '20px', letterSpacing: '0.22px' }}>
										{t('modal.recommended')}
									</span>
								)}
								{isSelected && (
									<div style={{ width: 40, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
											<path
												d="M13.5 4.5L6 12L2.5 8.5"
												stroke="var(--t-success, #4CD964)"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								)}
							</div>
						</button>
					);
				})}
				{filtered.length === 0 && (
					<div className="text-center py-10 text-[14px]" style={{ color: 'var(--t-text-muted)' }}>
						{t('modal.noPaymentMethods')}
					</div>
				)}
			</div>
		</>
	);
}
